// src/store/index.js
import axiosInstance from "@/axiosInstance";
import { createStore } from "vuex";
import {
  applyAccentColor,
  applyBrandColor,
  companyLogoUrl,
  DEFAULT_BRANDING,
} from "@/config/branding";
import { trialState } from "@/config/plans";
import { puo } from "@/config/roles";
import { leggiPiano, PIANO_START } from "@/utilities/piano";
import {
  conPassoVisto,
  leggiStato,
  senzaPassiDiRotta,
  statoVuoto,
} from "@/utilities/onboarding";
import {
  resetCompanyProfile,
  setCompanyLogo,
  setCompanyProfile,
} from "@/utilities/companyProfile";

/**
 * Scarica il logo e lo converte in data URL: jsPDF incorpora immagini solo in
 * quella forma, non da un URL.
 */
async function fetchLogoDataUrl(version = null) {
  try {
    const { data } = await axiosInstance.get(companyLogoUrl("", version), {
      responseType: "blob",
    });
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(data);
    });
  } catch (err) {
    console.error("logo aziendale non caricato:", err);
    return null;
  }
}

const store = createStore({
  state: {
    currentUser: null,
    // [sc-48350 / task 48396] Unità di vendita scelte in configurazione.
    // Elenco vuoto = istanza mai configurata: la scheda articolo le mostra
    // allora tutte, invece di presentare una tendina vuota. Sta nello stato e
    // non nel registro letto dai generatori PDF perché serve a un template, e
    // quel registro non è reattivo.
    companyProfileUnits: [],
    clients: [],
    operators: [],
    items: [],
    orders: [],
    ordersPagination: {},
    quotes: [],
    pagination: { next: null, previous: null },
    companyBranding: { ...DEFAULT_BRANDING },
    // [sc-48497 / task 48527] Stato della prova: arriva col profilo, che e'
    // gia' l'unica chiamata fatta a ogni accesso.
    companyTrial: {
      status: null,
      daysLeft: null,
      readOnly: false,
      deletionAt: null,
    },
    // [sc-48351] Piano, funzioni accese e tetti dell'azienda: arrivano col
    // profilo. Fino ad allora START, che non accende niente di PLUS.
    companyPiano: PIANO_START,
    // [sc-46314] Stato del tutorial dell'operatore, come arriva da /auth/me.
    // `null` finché il profilo non è arrivato: i componenti non mostrano
    // niente prima di allora, ed è così che si evita il lampo del modale.
    onboarding: null,
  },
  getters: {
    "items/list/filtered": (state) => (category) =>
      (state.items || []).filter((item) => item.category === category) ?? [],
    "items/list": (state) => state.items ?? [],
    "items/pagination": (state) => state.pagination,
    "clients/list": (state) => state.clients ?? [],
    "clients/pagination": (state) => state.pagination,
    "operators/list": (state) => state.operators ?? [],
    "quotes/list": (state) => state.quotes ?? [],
    "quotes/pagination": (state) => state.pagination,
    "orders/list": (state) => state.orders ?? [],
    "orders/pagination": (state) => state.ordersPagination,
    "company/branding": (state) => state.companyBranding,
    "company/trial": (state) => state.companyTrial,
    "company/readOnly": (state) => state.companyTrial.readOnly,
    // [sc-48351] Le pagine chiedono qui cosa è acceso, non a `features.js`.
    "company/plan": (state) => state.companyPiano.plan,
    "company/features": (state) => state.companyPiano.features,
    "company/limits": (state) => state.companyPiano.limits,
    "onboarding/stato": (state) => leggiStato(state.onboarding),
    // [sc-48351 / task 48786] Cosa può fare l'operatore collegato. Senza
    // `permissions` (API di prima) vale `isAdmin`: amministratore tutto,
    // gli altri niente.
    "auth/can": (state) => (area, azione) => {
      const u = state.currentUser;
      if (!u) return false;
      const permessi = u.permissions ?? {
        isAdmin: Boolean(u.isAdmin),
        grid: {},
      };
      return puo(permessi, area, azione);
    },
    "auth/role": (state) => state.currentUser?.role ?? null,
  },
  mutations: {
    SET_COMPANY_SALES_UNITS(state, units) {
      state.companyProfileUnits = Array.isArray(units) ? units : [];
    },
    SET_COMPANY_TRIAL(state, trial) {
      state.companyTrial = trialState(trial);
    },
    SET_COMPANY_PIANO(state, data) {
      state.companyPiano = leggiPiano(data);
    },
    SET_ONBOARDING(state, stato) {
      state.onboarding = stato ? leggiStato(stato) : null;
    },
    SET_COMPANY_BRANDING(state, branding) {
      state.companyBranding = { ...DEFAULT_BRANDING, ...(branding || {}) };
    },
    SET_CURRENT_USER(state, user) {
      state.currentUser = user;
    },
    CLEAR_CURRENT_USER(state) {
      state.currentUser = null;
      // L'intestazione dei documenti non deve sopravvivere alla sessione.
      resetCompanyProfile();
      // Il tutorial è della persona: uscendo non deve restare addosso alla
      // sessione di chi entra dopo sullo stesso browser.
      state.onboarding = null;
      // [sc-48351] Nemmeno il piano: è dell'azienda, non del browser.
      state.companyPiano = PIANO_START;
    },

    SET_CLIENTS(state, clients) {
      state.clients = clients;
    },
    // Nuova mutation per aggiungere al volo un cliente
    ADD_CLIENT(state, newClient) {
      state.clients.push(newClient);
    },
    // Nuova mutation per rimuovere un cliente
    REMOVE_CLIENT(state, clientId) {
      state.clients = state.clients.filter((client) => client.id !== clientId);
    },
    // Nuova mutaion per aggiornare un cliente esistente
    UPDATE_CLIENT(state, updatedClient) {
      const index = state.clients.findIndex(
        (client) => client.id === updatedClient.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.clients.splice(index, 1, updatedClient);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_OPERATORS(state, operators) {
      state.operators = operators;
    },
    // Nuova mutation per aggiungere al volo un operatore
    ADD_OPERATOR(state, newOperator) {
      state.operators.push(newOperator);
    },
    // Nuova mutation per rimuovere un operatore
    REMOVE_OPERATOR(state, operatorId) {
      state.operators = state.operators.filter((op) => op.id !== operatorId);
    },
    // Nuova mutaion per aggiornare un operatore esistente
    UPDATE_OPERATOR(state, updatedOperator) {
      const index = state.operators.findIndex(
        (op) => op.id === updatedOperator.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.operators.splice(index, 1, updatedOperator);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ITEMS(state, items) {
      state.items = items;
    },
    // Nuova mutation per la gestione della paginazione della lista degli articoli
    // SET_PAGINATION(state, pageInfo) {
    //   state.pagination = pageInfo;
    // },

    SET_PAGINATION(state, pageInfo) {
      state.pagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem: (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem: Math.min(
          pageInfo.currentPage * pageInfo.itemsPerPage,
          pageInfo.totalItems
        ),
      };
    },
    // Nuova mutation per aggiungere al volo un item
    ADD_ITEM(state, newItem) {
      state.items.push(newItem);
    },
    // Nuova mutation per rimuovere un item
    REMOVE_ITEM(state, itemId) {
      state.items = state.items.filter((item) => item.id !== itemId);
    },
    // Nuova mutaion per aggiornare un item esistente
    UPDATE_ITEM(state, updatedItem) {
      const index = state.items.findIndex((item) => item.id === updatedItem.id);
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.items.splice(index, 1, updatedItem);
      }
    },

    // ---------------------------------------------------------------------------//

    SET_ORDERS(state, orders) {
      state.orders = orders;
    },
    SET_ORDERS_PAGINATION(state, pageInfo) {
      state.ordersPagination = {
        currentPage: pageInfo.currentPage,
        totalPages: pageInfo.totalPages,
        totalItems: pageInfo.totalItems,
        itemsPerPage: pageInfo.itemsPerPage,
        startItem:
          pageInfo.startItem ??
          (pageInfo.currentPage - 1) * pageInfo.itemsPerPage + 1,
        endItem:
          pageInfo.endItem ??
          Math.min(
            pageInfo.currentPage * pageInfo.itemsPerPage,
            pageInfo.totalItems
          ),
      };
    },

    // ---------------------------------------------------------------------------//

    SET_QUOTES(state, quotes) {
      state.quotes = quotes;
    },
    // Nuova mutation per aggiungere al volo un quote
    ADD_QUOTE(state, newQuote) {
      state.quotes.push(newQuote);
    },
    // Nuova mutation per rimuovere un quote
    REMOVE_QUOTE(state, quoteId) {
      state.quotes = state.quotes.filter((quote) => quote.id !== quoteId);
    },
    // Nuova mutaion per aggiornare un quote esistente
    UPDATE_QUOTE(state, updatedQuote) {
      const index = state.quotes.findIndex(
        (quote) => quote.id === updatedQuote.id
      );
      if (index !== -1) {
        // Aggiorna i campi in modo reattivo
        state.quotes.splice(index, 1, updatedQuote);
      }
    },
    // Per aggiungere documenti al bucket
    ADD_DOC_QUOTE(state, { quoteId, document }) {
      const index = state.quotes.findIndex((q) => q.id === quoteId);
      if (index === -1) return;

      // se non c’è ancora l’array, lo creo
      if (!Array.isArray(state.quotes[index].documents)) {
        // Vue 3: ok aggiungere una nuova proprietà reattiva
        state.quotes[index].documents = [];
      }

      state.quotes[index].documents.push(document);
    },
  },

  actions: {
    /**
     * Salva lo stato del tutorial senza farsi sentire.
     *
     * [sc-46314] Fallire qui non è un evento: il peggio che può succedere è
     * che il giro si ripresenti al prossimo accesso. Un toast rosso perché
     * non è riuscito a segnare «ho visto la home» sarebbe assurdo, e
     * bloccare la navigazione lo sarebbe di più.
     *
     * `PATCH /auth/me` resta aperto anche a prova finita: non passa da
     * `blockWritesWhenExpired`, e l'interceptor qui sotto lascia passare
     * tutto ciò che sta sotto `/auth/`.
     */
    async salvaOnboarding({ commit, state }, stato) {
      commit("SET_ONBOARDING", stato);
      try {
        await axiosInstance.patch("/auth/me", { onboarding: state.onboarding });
      } catch (err) {
        console.warn("stato del tutorial non salvato:", err?.message);
      }
    },

    /**
     * [sc-46314] Rimostra pannellino e riflettore della rotta indicata.
     *
     * Non naviga: chi lo chiede sta su quella pagina, spesso con un modulo
     * mezzo compilato davanti.
     */
    spiegaQuestaPagina({ dispatch, getters }, routeName) {
      return dispatch(
        "salvaOnboarding",
        senzaPassiDiRotta(getters["onboarding/stato"], routeName)
      );
    },

    segnaPassoTutorial({ dispatch, getters }, passo) {
      return dispatch(
        "salvaOnboarding",
        conPassoVisto(getters["onboarding/stato"], passo)
      );
    },

    chiudiBenvenuto({ dispatch, getters }, { saltato } = {}) {
      const adesso = new Date().toISOString();
      const stato = { ...getters["onboarding/stato"] };
      if (saltato) stato.skippedAt = adesso;
      else stato.welcomeDoneAt = adesso;
      return dispatch("salvaOnboarding", stato);
    },

    /**
     * Rimette il tutorial da capo.
     *
     * [sc-46314] Senza questa, «Salta» sarebbe una porta a senso unico: chi
     * lo preme per sbaglio al primo accesso avrebbe perso il giro per
     * sempre, e l'unico modo di riaverlo sarebbe scrivere a noi.
     */
    riapriTutorial({ dispatch }) {
      return dispatch("salvaOnboarding", statoVuoto());
    },

    /**
     * Marchio dell'azienda, letto a ogni avvio.
     *
     * La rotta è pubblica di proposito: la pagina di login deve mostrare
     * l'identità dell'azienda prima che esista una sessione. Se l'API non
     * risponde l'applicazione parte lo stesso, con i valori predefiniti.
     */
    async fetchCompanyBranding({ commit }) {
      try {
        const { data } = await axiosInstance.get("/company-profile/branding");
        commit("SET_COMPANY_BRANDING", data);
        applyBrandColor(data?.brandColor);
        // Secondo colore, facoltativo: senza, `applyAccentColor` ripiega sul
        // grigio di sempre e l'interfaccia resta identica a com'era.
        applyAccentColor(data?.accentColor);
        return data;
      } catch (err) {
        console.error("fetchCompanyBranding failed:", err);
        commit("SET_COMPANY_BRANDING", null);
        return null;
      }
    },
    /**
     * Rimette il marchio di crmstart, senza chiedere niente all'API.
     *
     * [sc-48497] Serve alle pagine di piattaforma — accesso e registrazione —
     * che sono di tutti i clienti e di nessuno. Lo store può ancora avere il
     * marchio dell'azienda da cui si è appena usciti, e i colori sono
     * variabili CSS globali: senza questo azzeramento chi arriva a registrare
     * la propria azienda vede addosso alla pagina il marchio di un altro
     * cliente.
     */
    resetCompanyBranding({ commit }) {
      commit("SET_COMPANY_BRANDING", null);
      commit("SET_COMPANY_TRIAL", null);
      // [sc-48351] Il piano è dell'azienda da cui si esce: chi entra dopo
      // riparte da START finché non arriva il suo profilo.
      commit("SET_COMPANY_PIANO", null);
      applyBrandColor(DEFAULT_BRANDING.brandColor);
      applyAccentColor(DEFAULT_BRANDING.accentColor);
    },
    /**
     * Profilo completo dell'azienda: intestazione dei documenti e logo.
     *
     * Richiede una sessione, a differenza del marchio: contiene IBAN, partita
     * IVA e recapiti. Il risultato finisce nel registro letto dai generatori
     * PDF, che sono funzioni pure e non vedono lo store.
     */
    async fetchCompanyProfile({ commit }) {
      try {
        const { data } = await axiosInstance.get("/company-profile");
        setCompanyProfile(data);
        commit("SET_COMPANY_SALES_UNITS", data?.salesUnits);
        commit("SET_COMPANY_TRIAL", data);
        commit("SET_COMPANY_PIANO", data);
        // [sc-48497 / task 48522] Il marchio di chi è collegato arriva da qui,
        // non dalla rotta pubblica: quella serve la pagina di login, dove una
        // sessione non c'è ancora, e con più aziende sull'stessa installazione
        // non può sapere di quale parlare. Dentro l'applicazione lo sappiamo.
        commit("SET_COMPANY_BRANDING", {
          name: data?.name ?? null,
          subtitle: data?.subtitle ?? null,
          brandColor: data?.brandColor,
          accentColor: data?.accentColor ?? null,
          productCategories: data?.productCategories ?? null,
          itemCategoriesInUse: Array.isArray(data?.itemCategoriesInUse)
            ? data.itemCategoriesInUse
            : [],
          hasLogo: Boolean(data?.hasLogo),
          logoVersion: data?.logoVersion ?? null,
          configured: Boolean(data?.configuredAt),
        });
        // I colori vanno anche applicati, non solo riposti: dopo un accesso
        // senza ricaricare la pagina, questa è l'unica chiamata che passa, e
        // senza queste due righe l'interfaccia resterebbe di crmstart.
        applyBrandColor(data?.brandColor);
        applyAccentColor(data?.accentColor);
        setCompanyLogo(
          data?.hasLogo ? await fetchLogoDataUrl(data.logoVersion) : null
        );
        return data;
      } catch (err) {
        console.error("fetchCompanyProfile failed:", err);
        return null;
      }
    },
    async fetchClients({ commit }, { page = 1, limit, search = "" } = {}) {
      try {
        const { data } = await axiosInstance.get("/client/get-all", {
          params: { page, limit, ...(search ? { search } : {}) },
        });
        commit("SET_CLIENTS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun cliente”: trasformalo in array vuoto
          commit("SET_CLIENTS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        console.error("fetchClients failed:", err);
        throw err; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per creare un cliente
    async createClient({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("/client/add", payload);

        // commit("ADD_CLIENT", response.data.operator);
        await dispatch("fetchClients");
        return response;
      } catch (error) {
        console.error("Errore durante la creazione di un nuovo client:", error);
        throw error;
      }
    },

    // Nuova azione per aggiornare un cliente esistente
    async updateClient({ commit }, { clientId, payload }) {
      const response = await axiosInstance.patch(
        `client/update/${clientId}`,
        payload
      );
      commit("UPDATE_CLIENT", response.data.client);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) il cliente
    async archiveClient({ commit }, clientId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`client/archive/${clientId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_CLIENT", clientId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione del cliente: ", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOperators({ commit }) {
      try {
        const response = await axiosInstance.get("/auth/get-all"); // endpoint della tua API
        commit("SET_OPERATORS", response.data);
      } catch (error) {
        console.error("Errore durante il fetch degli operators:", error);
        throw error;
      }
    },

    // Nuova azione per creare un operatore
    async createOperator({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post(
          "auth/create-operator",
          payload
        );

        // commit("ADD_OPERATOR", response.data.operator);
        await dispatch("fetchOperators");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo operator:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un operatore esistente
    async updateOperator({ commit }, { operatorId, payload }) {
      const response = await axiosInstance.patch(
        `auth/update/${operatorId}`,
        payload
      );
      commit("UPDATE_OPERATOR", response.data.operator);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’operatore
    async archiveOperator({ commit }, operatorId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`auth/archive/${operatorId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_OPERATOR", operatorId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione di un operator:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchItems(
      { commit },
      {
        page = 1,
        limit,
        category = null,
        subCategory = "",
        search = "",
        itemCode = "",
      } = {}
    ) {
      try {
        const { data } = await axiosInstance.get("/item/get-all", {
          params: {
            page,
            limit,
            // [sc-48583 / task 48599] `category` può essere una stringa o un
            // elenco: una scheda dei listini raccoglie più merceologie, e
            // axios le manda come `category[]=...`, che l'API traduce in un IN.
            ...(Array.isArray(category)
              ? category.length
                ? { category }
                : {}
              : category
              ? { category }
              : {}),
            ...(subCategory ? { subCategory } : {}),
            ...(search ? { search } : {}),
            ...(itemCode ? { itemCode } : {}),
          },
        });
        commit("SET_ITEMS", Array.isArray(data.results) ? data.results : []);
        commit("SET_PAGINATION", {
          // first: data.first ?? null,
          // next: data.next ?? null,
          // previous: data.previous ?? null,
          // last: data.last ?? null,
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (error) {
        if (error.response?.status === 404) {
          // il server dice “nessun articolo: trasformalo in array vuoto
          commit("SET_ITEMS", []);
          // azzera la paginazione per coerenza UI
          commit("SET_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: 8,
          });
          return [];
        }
        // console.error("fetchClients failed:", error);
        throw error; // lascia passare altri errori (500, 401…)
      }
    },

    // Nuova azione per fetchare gli articoli venduti ai clienti
    async fetchClientSoldItems(_, clientId) {
      try {
        const response = await axiosInstance.get(
          `client/${clientId}/client-info`
        );
        return response.data;
      } catch (error) {
        console.error(
          "Errore durante il caricamento degli articoli venduti associati al cliente",
          error
        );
        throw error;
      }
    },

    // Nuova azione per creare un item
    async createItem({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("item/create", payload);

        // commit("ADD_ITEM", response.data.item);
        await dispatch("fetchItems");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo articolo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per aggiornare un item esistente
    async updateItem({ commit }, { itemId, payload }) {
      const response = await axiosInstance.patch(
        `item/update/${itemId}`,
        payload
      );
      commit("UPDATE_ITEM", response.data.item);
      return response;
    },

    // Aggiungiamo un’azione per archiviare (o “eliminare”) l’item
    async archiveItem({ commit }, itemId) {
      try {
        // Faccio la chiamata al backend
        const response = await axiosInstance.post(`item/archive/${itemId}`);
        // Se va a buon fine, aggiorno localmente lo store
        commit("REMOVE_ITEM", itemId);
        return response;
      } catch (error) {
        console.error("Errore durante l'archiviazione dell'articolo:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------------------//

    async fetchOrders({ commit }, { page = 1, limit = 10, status } = {}) {
      try {
        const { data } = await axiosInstance.get("/order/get-all", {
          params: {
            page,
            limit,
            ...(status ? { status } : {}),
          },
        });
        commit("SET_ORDERS", Array.isArray(data.results) ? data.results : []);
        commit("SET_ORDERS_PAGINATION", data.pagination);
        return data.results;
      } catch (err) {
        if (err.response?.status === 404) {
          commit("SET_ORDERS", []);
          commit("SET_ORDERS_PAGINATION", {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            itemsPerPage: limit,
            startItem: 0,
            endItem: 0,
          });
          return [];
        }
        throw err;
      }
    },

    async createOrder(_, payload) {
      const response = await axiosInstance.post("order/create", payload);
      return response;
    },

    async updateOrder(_, { id, payload }) {
      const response = await axiosInstance.patch(`order/update/${id}`, payload);
      return response;
    },

    async archiveOrder(_, orderId) {
      const response = await axiosInstance.post(`order/archive/${orderId}`);
      return response;
    },

    async splitOrder(_, { id, confirmedQty, suspendedQty }) {
      const response = await axiosInstance.patch(`order/${id}/split`, {
        confirmedQty,
        suspendedQty,
      });
      return response;
    },

    async completeOrder(_, { id, damagedQty = 0 }) {
      const response = await axiosInstance.patch(`order/${id}/complete`, {
        damagedQty,
      });
      return response;
    },

    async fetchActiveOrders({ commit }) {
      const statuses = ["creato", "in_sospeso", "confermato"];
      const results = await Promise.all(
        statuses.map((status) =>
          axiosInstance
            .get("/order/get-all", { params: { status, limit: 10000 } })
            .then((r) => (Array.isArray(r.data.results) ? r.data.results : []))
            .catch((err) =>
              err.response?.status === 404 ? [] : Promise.reject(err)
            )
        )
      );
      const merged = results
        .flat()
        .sort((a, b) => new Date(b.orderedAt) - new Date(a.orderedAt));
      commit("SET_ORDERS", merged);
      commit("SET_ORDERS_PAGINATION", {
        currentPage: 1,
        totalPages: 1,
        totalItems: merged.length,
        itemsPerPage: merged.length,
        startItem: merged.length ? 1 : 0,
        endItem: merged.length,
      });
      return merged;
    },

    // ---------------------------------------------------------------------------//

    async fetchQuotes(
      { commit, getters },
      { page = 1, limit = 7, search = "", archived, isConfirmed, bozze } = {}
    ) {
      // [sc-48351 / task 48786] Senza filtro l'elenco mescola preventivi e
      // ordini, e l'API lo dà solo a chi li vede tutti e due. Chi ne vede uno
      // solo chiede quello: è il caso di «Utente limitato», il ruolo con cui
      // nasce ogni invitato.
      if (isConfirmed === undefined) {
        const can = getters["auth/can"];
        const preventivi = can("preventivi", "V");
        const ordini = can("ordini", "V");
        if (preventivi && !ordini) isConfirmed = false;
        else if (ordini && !preventivi) isConfirmed = true;
      }
      try {
        const { data } = await axiosInstance.get("/quote/get-all", {
          params: {
            page,
            limit,
            ...(search ? { search } : {}),
            ...(archived !== undefined ? { archived } : {}), // se non è definito, non lo mando
            ...(isConfirmed !== undefined ? { isConfirmed } : {}),
            // [sc-48351 / task 7] La scheda «Bozze». Senza il parametro
            // l'elenco è quello di sempre, meno le bozze: il filtro lo fa
            // l'API, così la pagina non pagina su un insieme e ne mostra un
            // altro.
            ...(bozze ? { bozze: true } : {}),
          },
        });
        // console.log("fetchQuotes data", data);
        // console.log("allquotes", data);
        commit("SET_QUOTES", data.results);
        commit("SET_PAGINATION", {
          currentPage: data.pagination.currentPage,
          totalPages: data.pagination.totalPages,
          totalItems: data.pagination.totalItems,
          itemsPerPage: data.pagination.itemsPerPage,
        });
        return data.results; // così il componente può usarlo
      } catch (err) {
        if (err.response?.status === 404) {
          // il server dice “nessun preventivo”: trasformalo in array vuoto
          commit("SET_QUOTES", []);
          throw err;
        }
        console.error("Errore durante il fetch dei preventivi:", err);
        throw err;
      }
    }, // quando verranno usate le rotte del backend

    /**
     * [sc-48351 / task 7] Il passaggio di stato di una bozza (§2.7).
     *
     * Non aggiorna la riga a mano: ricarica l'elenco. Lo stato cambia anche
     * l'insieme a cui il preventivo appartiene — una bozza che diventa
     * «pronto» esce dalla scheda «Bozze» — e una riga ritoccata sul posto
     * resterebbe dove non deve stare.
     */
    async setOfferState(_ctx, { quoteId, offerState }) {
      const { data } = await axiosInstance.patch(
        `quote/${quoteId}/offer-state`,
        { offerState }
      );
      return data;
    },

    // Nuova azione per creare un quote
    async createQuote({ dispatch }, payload) {
      try {
        const response = await axiosInstance.post("quote/create", payload);

        // commit("ADD_QUOTE", response.data.quote);
        await dispatch("fetchQuotes");
        return response;
      } catch (error) {
        console.error(
          "Errore durante la creazione di un nuovo preventivo:",
          error
        );
        throw error;
      }
    },

    // Nuova azione per caricare un documento di un quote
    async uploadQuoteDocument({ commit }, { quoteId, document, filename }) {
      try {
        const formData = new FormData();
        formData.append("document", document, filename);

        // POST multipart/form-data alla tua rotta
        // Nota: axiosInstance ha baseURL impostato su '/api'
        // Nessuna intestazione a mano: scritta a mano manca il boundary, e
        // senza quello il server non sa dove comincia il file. La scrive il
        // browser. [sc-48350 / task 48396]
        const response = await axiosInstance.post(
          `/upload/quotes/${quoteId}/documents`,
          formData
        );

        // Il backend restituisce il record creato sotto la chiave "doc"
        // (fallback su "document" per compatibilità). Commit solo se presente,
        // così non inseriamo valori vuoti nello store.
        const uploadedDoc = response.data?.doc || response.data?.document;
        if (uploadedDoc) {
          commit("ADD_DOC_QUOTE", {
            quoteId,
            document: uploadedDoc,
          });
        }
        return response;
      } catch (error) {
        console.error("Errore durante l'upload del documento:", error);
        throw error;
      }
    },

    async sendQuoteDocumentEmail(
      _context,
      { quoteId, objectName, contact = "" }
    ) {
      return await axiosInstance.post(
        `/upload/quotes/${quoteId}/documents/send-email`,
        { objectName, contact }
      );
    },

    // Nuova azione per aggiornare un quote esistente
    async updateQuote({ commit }, { quoteId, payload }) {
      const response = await axiosInstance.patch(
        `quote/update-with-delta/${quoteId}`,
        payload
      );
      commit("UPDATE_QUOTE", response.data.quote);
      return response;
    },

    // Nuova azione per verificare se ci sono preventivi archiviati
    async hasArchivedQuotes({ getters }) {
      // [sc-48351 / task 48786] Senza filtro l'elenco comprende gli ordini, e
      // l'API lo rifiuta a chi non li vede: si chiedono solo i preventivi.
      const can = getters["auth/can"];
      if (!can("preventivi", "V")) return false;
      const params = { page: 1, limit: 1, archived: true };
      if (!can("ordini", "V")) params.isConfirmed = false;
      try {
        const { data } = await axiosInstance.get("/quote/get-all", { params });

        /* nuovo formato: { results: [ … ], pagination: { totalItems, … } } */
        return (data.pagination?.totalItems || 0) > 0;
      } catch (err) {
        console.error(
          "Errore durante il tentantivo di ritrovare i preventivi archiviati: ",
          err
        );
        return false; // fallback sicuro
      }
    },

    // ✅ archivia preventivo
    async archiveQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: true,
        });
        // rimuovo dalla lista corrente (in "attivi" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante l'archiviazione del preventivo:", error);
        throw error;
      }
    },

    // ✅ ripristina preventivo (nuova azione)
    async restoreQuote({ commit }, quoteId) {
      try {
        const { data } = await axiosInstance.post(`quote/archive/${quoteId}`, {
          archived: false,
        });
        // rimuovo dalla lista corrente (in "archiviati" non deve più vedersi)
        commit("REMOVE_QUOTE", quoteId);
        return { data }; // data = { message, quote }
      } catch (error) {
        console.error("Errore durante il ripristino del preventivo:", error);
        throw error;
      }
    },
  },
});

export default store;
